/** Prescrição de refração (grau de óculos) — evolução oftalmológica. */

export interface OphthalmologyRefractionValues {
  odSph: string;
  odCyl: string;
  odAxis: string;
  osSph: string;
  osCyl: string;
  osAxis: string;
  /** Adição esférica para perto, ambos os olhos. */
  nearAdd: string;
  notes: string;
}

export const DEFAULT_REFRACTION_NOTES = '1. Medir DNP';

export function emptyOphthalmologyRefraction(): OphthalmologyRefractionValues {
  return {
    odSph: '',
    odCyl: '',
    odAxis: '',
    osSph: '',
    osCyl: '',
    osAxis: '',
    nearAdd: '',
    notes: DEFAULT_REFRACTION_NOTES,
  };
}

export function refractionHasContent(v: OphthalmologyRefractionValues): boolean {
  return Boolean(
    v.odSph.trim() ||
      v.odCyl.trim() ||
      v.odAxis.trim() ||
      v.osSph.trim() ||
      v.osCyl.trim() ||
      v.osAxis.trim() ||
      v.nearAdd.trim() ||
      v.notes.trim(),
  );
}

export function serializeRefractionBody(v: OphthalmologyRefractionValues): string {
  return JSON.stringify({
    odSph: v.odSph.trim(),
    odCyl: v.odCyl.trim(),
    odAxis: v.odAxis.trim(),
    osSph: v.osSph.trim(),
    osCyl: v.osCyl.trim(),
    osAxis: v.osAxis.trim(),
    nearAdd: v.nearAdd.trim(),
    notes: v.notes,
  });
}

export function parseRefractionBody(raw: string): OphthalmologyRefractionValues {
  const fallback = emptyOphthalmologyRefraction();
  const text = String(raw || '').trim();
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        odSph: String(parsed.odSph ?? ''),
        odCyl: String(parsed.odCyl ?? ''),
        odAxis: String(parsed.odAxis ?? ''),
        osSph: String(parsed.osSph ?? ''),
        osCyl: String(parsed.osCyl ?? ''),
        osAxis: String(parsed.osAxis ?? ''),
        nearAdd: String(parsed.nearAdd ?? ''),
        notes: String(parsed.notes ?? ''),
      };
    }
  } catch {
    /* texto legado: trata o corpo inteiro como observações */
  }
  return { ...fallback, notes: text };
}

export const EXAM_ORDER_REFRACTION_MARKER_START = '<!--KLINVIA_REFRACTION-->';
export const EXAM_ORDER_REFRACTION_MARKER_END = '<!--/KLINVIA_REFRACTION-->';

/** Anexa refração estruturada ao corpo da solicitação de exames (para impressão com grade). */
export function embedRefractionInExamOrderBody(
  baseBody: string,
  refraction: OphthalmologyRefractionValues | null | undefined,
): string {
  if (!refraction || !refractionHasContent(refraction)) return baseBody.trim();
  return `${baseBody.trim()}\n\n${EXAM_ORDER_REFRACTION_MARKER_START}${serializeRefractionBody(refraction)}${EXAM_ORDER_REFRACTION_MARKER_END}`;
}

/** Separa refração embutida do texto legível da solicitação de exames. */
export function extractRefractionFromExamOrderBody(body: string): {
  bodyWithoutMarker: string;
  refraction: OphthalmologyRefractionValues | null;
} {
  const text = String(body || '');
  const re = new RegExp(
    `${EXAM_ORDER_REFRACTION_MARKER_START.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([\\s\\S]*?)${EXAM_ORDER_REFRACTION_MARKER_END.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
  );
  const match = text.match(re);
  if (!match) return { bodyWithoutMarker: text.trim(), refraction: null };
  return {
    bodyWithoutMarker: text.replace(match[0], '').trim(),
    refraction: parseRefractionBody(match[1]),
  };
}

/** Cola da aba Refração ou JSON interno → valores estruturados. */
export function tryParseRefractionFromPaste(text: string): OphthalmologyRefractionValues | null {
  const raw = String(text || '').trim();
  if (!raw) return null;

  if (raw.startsWith('{')) {
    const parsed = parseRefractionBody(raw);
    if (refractionHasContent(parsed)) return parsed;
  }

  const pick = (re: RegExp) => {
    const m = raw.match(re);
    const v = m?.[1]?.trim();
    return v && v !== '—' ? v : '';
  };

  const odSph = pick(/Olho direito\s+Esf\s+(\S+)/i);
  const odCyl = pick(/Olho direito\s+Esf\s+\S+\s+Cil\s+(\S+)/i);
  const odAxis = pick(/Olho direito\s+Esf\s+\S+\s+Cil\s+\S+\s+Eixo\s+(\S+)/i);
  const osSph = pick(/Olho esquerdo\s+Esf\s+(\S+)/i);
  const osCyl = pick(/Olho esquerdo\s+Esf\s+\S+\s+Cil\s+(\S+)/i);
  const osAxis = pick(/Olho esquerdo\s+Esf\s+\S+\s+Cil\s+\S+\s+Eixo\s+(\S+)/i);
  const nearAdd = pick(/Para perto \(adição AO\):\s*(\S+)/i);

  const candidate: OphthalmologyRefractionValues = {
    ...emptyOphthalmologyRefraction(),
    odSph,
    odCyl,
    odAxis,
    osSph,
    osCyl,
    osAxis,
    nearAdd,
    notes: '',
  };

  return refractionHasContent(candidate) ? candidate : null;
}

/** Prévia em texto para chips e visualização da evolução (sem o JSON interno). */
export function formatRefractionPreview(raw: string): string {
  const v = parseRefractionBody(raw);
  const lines = [
    'Para longe',
    `Olho direito  Esf ${v.odSph || '—'}  Cil ${v.odCyl || '—'}  Eixo ${v.odAxis || '—'}`,
    `Olho esquerdo  Esf ${v.osSph || '—'}  Cil ${v.osCyl || '—'}  Eixo ${v.osAxis || '—'}`,
    `Para perto (adição AO): ${v.nearAdd || '—'}`,
  ];
  if (v.notes.trim()) {
    lines.push('', 'Observações:', v.notes.trim());
  }
  return lines.join('\n');
}
